import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import AnimateHeight from "react-animate-height";
import { useDebouncedCallback } from "use-debounce";
import { AreaChart, WealthCalculatorPeriodAdjustment } from "../../../shared/components/Graphs/Area/AreaGraph";
import { BlockContentRenderer } from "../BlockContentRenderer";
import { LoadingButtonSpinner } from "../../../shared/components/Spinner/LoadingButtonSpinner";
import { wealthMountainGraphData } from "./data";
import styles from "./WealthCalculator.module.scss";
import { WealthCalculatorInput, WealthCalculatorInputConfiguration } from "./WealthCalculatorInput";
import { TaxJurisdiction, calculateWealthPercentile, equvivalizeIncome, getCachedPostTaxIncome, getEstimatedPostTaxIncome } from "./_util";
import { WealthCalculatorSlider, WealthCalculatorSliderConfig } from "./WealthCalculatorSlider";
import { WealthCalculatorImpact, WealthCalculatorImpactConfig } from "./WealthCalculatorImpact";
import { AdjustedPPPFactorResult, getDanishAdjustedPPPconversionFactor, getNorwegianAdjustedPPPconversionFactor, getSwedishAdjustedPPPconversionFactor } from "./_queries";
export type WealthCalculatorConfiguration = {
  calculator_input_configuration: WealthCalculatorInputConfiguration;
  slider_configuration: WealthCalculatorSliderConfig;
  income_percentile_label_template_string: string;
  income_percentile_after_donation_label_template_string: string;
  default_donation_percentage?: number;
  data_explanation_label?: string;
  data_explanation?: any;
  x_axis_label?: string;
  chart_label?: string;
};
type WealthCalculatorProps = {
  title: string;
  configuration: WealthCalculatorConfiguration;
  impactConfiguration?: WealthCalculatorImpactConfig;
  periodAdjustment: WealthCalculatorPeriodAdjustment;
  locale: string;
};
export const WealthCalculator: React.FC<WealthCalculatorProps> = t0 => {
  const $ = _c(86);
  const {
    title,
    configuration,
    impactConfiguration,
    periodAdjustment,
    locale
  } = t0;
  const {
    calculator_input_configuration,
    slider_configuration,
    income_percentile_label_template_string,
    income_percentile_after_donation_label_template_string,
    default_donation_percentage,
    data_explanation_label,
    data_explanation,
    x_axis_label,
    chart_label
  } = configuration;
  const [incomeInputs, setIncomeInputs] = useState();
  let t1;
  if ($[0] !== incomeInputs) {
    t1 = incomeInputs || [0];
    $[0] = incomeInputs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const incomes = t1;
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [numberOfAdults, setNumberOfParents] = useState(1);
  const [donationPercentage, setDonationPercentage] = useState(default_donation_percentage || 10);
  const [loadingPostTaxIncome, setLoadingPostTaxIncome] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      postTaxIncome: 0,
      numberOfAdults: 1
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [postTaxEstimate, setPostTaxEstimate] = useState(t2);
  const postTaxIncome = postTaxEstimate.postTaxIncome;
  const latestEstimateRequest = useRef(0);
  const [explanationOpen, setExplanationOpen] = useState(false);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      adjustedPPPfactor: 7,
      cumulativeInflation: 0,
      pppFactor: 7
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [pppConversion, setPppConversion] = useState(t3);
  let t4;
  let t5;
  if ($[4] !== locale) {
    t4 = () => {
      if (locale === "no") {
        getNorwegianAdjustedPPPconversionFactor().then(res => {
          setPppConversion(res);
        });
      } else {
        if (locale === "sv") {
          getSwedishAdjustedPPPconversionFactor().then(res_0 => {
            setPppConversion(res_0);
          });
        } else {
          if (locale === "dk") {
            getDanishAdjustedPPPconversionFactor().then(res_1 => {
              setPppConversion(res_1);
            });
          } else {
            console.error("Unsupported locale", locale);
          }
        }
      }
    };
    t5 = [locale];
    $[4] = locale;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  const taxJurisdiction = locale === "no" ? TaxJurisdiction.NO : locale === "sv" ? TaxJurisdiction.SV : locale === "dk" ? TaxJurisdiction.DK : undefined;
  const sumIncomes = _temp2;
  let t6;
  if ($[7] !== incomes || $[8] !== locale || $[9] !== periodAdjustment || $[10] !== taxJurisdiction) {
    t6 = (requestId, adults) => {
      if (!taxJurisdiction) {
        console.error("Unsupported locale", locale);
        return;
      }
      Promise.all(incomes.map(adultIncome => getEstimatedPostTaxIncome(adultIncome, periodAdjustment, taxJurisdiction))).then(postTaxIncomes => {
        if (requestId !== latestEstimateRequest.current) {
          return;
        }
        setPostTaxEstimate({
          postTaxIncome: sumIncomes(postTaxIncomes),
          numberOfAdults: adults
        });
      }).catch(_temp3).finally(() => {
        if (requestId === latestEstimateRequest.current) {
          setLoadingPostTaxIncome(false);
        }
      });
    };
    $[7] = incomes;
    $[8] = locale;
    $[9] = periodAdjustment;
    $[10] = taxJurisdiction;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const calculatePostTaxIncome = useDebouncedCallback(t6, 250);
  let t7;
  if ($[12] !== incomeInputs || $[13] !== numberOfAdults) {
    t7 = {
      incomeInputs,
      numberOfAdults,
      cached: true
    };
    $[12] = incomeInputs;
    $[13] = numberOfAdults;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const [estimateInputs, setEstimateInputs] = useState(t7);
  if (estimateInputs.incomeInputs !== incomeInputs || estimateInputs.numberOfAdults !== numberOfAdults) {
    const cached = taxJurisdiction ? incomes.map(adultIncome_0 => getCachedPostTaxIncome(adultIncome_0, periodAdjustment, taxJurisdiction)) : [];
    const allCached = !!taxJurisdiction && cached.every(_temp4);
    setEstimateInputs({
      incomeInputs,
      numberOfAdults,
      cached: allCached
    });
    if (allCached) {
      setPostTaxEstimate({
        postTaxIncome: sumIncomes(cached as number[]),
        numberOfAdults
      });
      setLoadingPostTaxIncome(false);
    } else {
      setLoadingPostTaxIncome(true);
    }
  }
  let t8;
  if ($[15] !== calculatePostTaxIncome || $[16] !== estimateInputs.cached || $[17] !== estimateInputs.numberOfAdults) {
    t8 = () => {
      const requestId_0 = latestEstimateRequest.current = latestEstimateRequest.current + 1;
      if (estimateInputs.cached) {
        calculatePostTaxIncome.cancel();
        return;
      }
      calculatePostTaxIncome(requestId_0, estimateInputs.numberOfAdults);
    };
    $[15] = calculatePostTaxIncome;
    $[16] = estimateInputs.cached;
    $[17] = estimateInputs.numberOfAdults;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== calculatePostTaxIncome || $[20] !== estimateInputs) {
    t9 = [estimateInputs, calculatePostTaxIncome];
    $[19] = calculatePostTaxIncome;
    $[20] = estimateInputs;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  useEffect(t8, t9);
  const equvivalizedIncome = equvivalizeIncome(postTaxIncome, numberOfChildren, postTaxEstimate.numberOfAdults);
  let t10;
  if ($[22] !== calculator_input_configuration || $[23] !== incomeInputs || $[24] !== loadingPostTaxIncome || $[25] !== numberOfAdults || $[26] !== numberOfChildren || $[27] !== title) {
    t10 = <WealthCalculatorInput title={title} incomeInput={incomeInputs} setIncomeInput={setIncomeInputs} numberOfChildren={numberOfChildren} setNumberOfChildren={setNumberOfChildren} numberOfAdults={numberOfAdults} setNumberOfParents={setNumberOfParents} loadingPostTaxIncome={loadingPostTaxIncome} config={calculator_input_configuration} />;
    $[22] = calculator_input_configuration;
    $[23] = incomeInputs;
    $[24] = loadingPostTaxIncome;
    $[25] = numberOfAdults;
    $[26] = numberOfChildren;
    $[27] = title;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== donationPercentage || $[30] !== equvivalizedIncome || $[31] !== periodAdjustment || $[32] !== postTaxIncome || $[33] !== pppConversion.adjustedPPPfactor || $[34] !== slider_configuration) {
    t11 = <WealthCalculatorSlider donationPercentage={donationPercentage} setDonationPercentage={setDonationPercentage} postTaxIncome={postTaxIncome} wealthMountainGraphData={wealthMountainGraphData} equvivalizedIncome={equvivalizedIncome} periodAdjustment={periodAdjustment} adjustedPppFactor={pppConversion.adjustedPPPfactor} config={slider_configuration} />;
    $[29] = donationPercentage;
    $[30] = equvivalizedIncome;
    $[31] = periodAdjustment;
    $[32] = postTaxIncome;
    $[33] = pppConversion.adjustedPPPfactor;
    $[34] = slider_configuration;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const t12 = equvivalizedIncome || 0;
  const t13 = donationPercentage / 100;
  const t14 = equvivalizedIncome || 0;
  const t15 = pppConversion?.adjustedPPPfactor;
  let t16;
  if ($[36] !== periodAdjustment || $[37] !== t14 || $[38] !== t15) {
    t16 = calculateWealthPercentile(wealthMountainGraphData, t14, periodAdjustment, t15);
    $[36] = periodAdjustment;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const t17 = equvivalizedIncome * (1 - donationPercentage / 100);
  const t18 = pppConversion?.adjustedPPPfactor;
  let t19;
  if ($[40] !== periodAdjustment || $[41] !== t17 || $[42] !== t18) {
    t19 = calculateWealthPercentile(wealthMountainGraphData, t17, periodAdjustment, t18);
    $[40] = periodAdjustment;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const t20 = pppConversion?.adjustedPPPfactor;
  let t21;
  if ($[44] !== chart_label || $[45] !== income_percentile_after_donation_label_template_string || $[46] !== income_percentile_label_template_string || $[47] !== periodAdjustment || $[48] !== t12 || $[49] !== t13 || $[50] !== t16 || $[51] !== t19 || $[52] !== t20) {
    t21 = <div className={styles.calculator__output__chart}><AreaChart data={wealthMountainGraphData} lineInput={t12} donationPercentage={t13} wealthPercentile={t16} afterDonationWealthPercentile={t19} label={chart_label} afterDonationPercentileLabelTemplateString={income_percentile_after_donation_label_template_string} incomePercentileLabelTemplateString={income_percentile_label_template_string} adjustedPPPConversionFactor={t20} periodAdjustment={periodAdjustment} /></div>;
    $[44] = chart_label;
    $[45] = income_percentile_after_donation_label_template_string;
    $[46] = income_percentile_label_template_string;
    $[47] = periodAdjustment;
    $[48] = t12;
    $[49] = t13;
    $[50] = t16;
    $[51] = t19;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== loadingPostTaxIncome) {
    t22 = loadingPostTaxIncome && <div className={styles.calculator__output__spinner} data-cy="wealthcalculator-loading-spinner"><LoadingButtonSpinner /></div>;
    $[54] = loadingPostTaxIncome;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t21 || $[57] !== t22) {
    t23 = <div className={styles.calculator__output} data-cy="wealthcalculator-graph">{t21}{t22}</div>;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const t24 = styles.calculator__explanation__toggle + " " + (explanationOpen ? styles.calculator__explanation__toggle_open : "");
  let t25;
  if ($[59] !== explanationOpen) {
    t25 = () => setExplanationOpen(!explanationOpen);
    $[59] = explanationOpen;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  const t26 = data_explanation_label || "Explanation";
  let t27;
  if ($[61] !== t24 || $[62] !== t25 || $[63] !== t26) {
    t27 = <div className={t24} data-cy="wealthcalculator-explanation-toggle" onClick={t25}>{t26}</div>;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const t28 = x_axis_label || "Yearly income (log scale)";
  let t29;
  if ($[65] !== t28) {
    t29 = <div className={styles.calculator__axis__label}><span>{t28} →</span></div>;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t10 || $[68] !== t11 || $[69] !== t23 || $[70] !== t27 || $[71] !== t29) {
    t30 = <div className={styles.calculator} data-cy="wealthcalculator-container">{t10}{t11}{t23}{t27}{t29}</div>;
    $[67] = t10;
    $[68] = t11;
    $[69] = t23;
    $[70] = t27;
    $[71] = t29;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  const t31 = explanationOpen ? "auto" : 0;
  let t32;
  if ($[73] !== data_explanation) {
    t32 = <div data-cy="wealthcalculator-explanation"><BlockContentRenderer content={[data_explanation]} /></div>;
    $[73] = data_explanation;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== t31 || $[76] !== t32) {
    t33 = <AnimateHeight height={t31} duration={500}>{t32}</AnimateHeight>;
    $[75] = t31;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== donationPercentage || $[79] !== impactConfiguration || $[80] !== postTaxIncome) {
    t34 = impactConfiguration && <WealthCalculatorImpact donationPercentage={donationPercentage} setDonationPercentage={setDonationPercentage} postTaxIncome={postTaxIncome} config={{
      ...impactConfiguration,
      intervention_configuration: {
        ...impactConfiguration.intervention_configuration,
        output_configuration: {
          ...impactConfiguration.intervention_configuration.output_configuration,
          donate_button: false
        }
      }
    }} />;
    $[78] = donationPercentage;
    $[79] = impactConfiguration;
    $[80] = postTaxIncome;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== t30 || $[83] !== t33 || $[84] !== t34) {
    t35 = <div className={styles.wrapper}>{t30}{t33}{t34}</div>;
    $[82] = t30;
    $[83] = t33;
    $[84] = t34;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  return t35;
};
function _temp(total, value) {
  return total + value;
}
function _temp2(values) {
  return values.reduce(_temp, 0);
}
function _temp3(error) {
  console.error("Failed to calculate post-tax income", error);
}
function _temp4(value_0) {
  return typeof value_0 !== "undefined";
}